import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * A render crash must not blank the app.
 *
 * This is offline-first software: the moment it is most likely to hit an
 * unexpected shape is the moment it is holding a stale cached response with no
 * network to go and check. A white screen then is the worst possible outcome —
 * it looks identical to the app being broken, with no way to recover.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('render failed', error, info.componentStack)
  }

  override render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <div className="tabular text-[10px] uppercase tracking-[0.14em] text-[var(--text-subtle)]">
          Something broke
        </div>
        <p className="mt-2 text-[14px] text-[var(--text)]">
          This screen failed to render. Your queued changes are safe.
        </p>
        <p className="tabular mt-2 text-[12px] text-[var(--text-muted)]">{this.state.error.message}</p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="tap mt-4 rounded-[var(--radius)] border border-[var(--border-strong)] px-4 py-2 text-[14px] text-[var(--text)]"
        >
          Try again
        </button>
      </div>
    )
  }
}
